import { Suspense, useContext, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Loading from "@/common/components/ui/loading/Loading";
import {
  TableFilters,
  TableFiltersState,
} from "@/common/components/ui/table/TableFilters";
import GenericTable from "@/common/components/ui/table/GenericTable";
import TableFooter from "@/common/components/ui/table/TableFooter";
import { useToastContext } from "@/common/components/ui/toast/toast.context";
import ContextMenu from "@/common/components/ui/context-menu/ContextMenu";
import { getCollisions } from "@/common/services/collision.api";
import { Collision } from "@/types/collision.type";
import { formatDateAndTime } from "@/common/helpers/date.helpers";
import { getCompanySigla } from "@/common/helpers/company.helpers";
import { AuthContext } from "@/common/components/auth/authContext/AuthContext";
import { useCompanyStore } from "@/common/states/store/Company";
import { getStatusLabelsVehicle } from "@/modules/vehicles/components/TableVehicles";
import { VEHICLE_STATUS } from "@/types/vehicle.type";

const headers = {
  city: "Cidade",
  licensePlate: "Placa",
  partnerName: "Parceiro",
  status: "Status do veículo",
  value: "Valor do dano",
  pieces: "Qtd. peças",
  createdAt: "Criado em",
  actions: "",
};

function toNumber(value: unknown, fallback: number) {
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? n : fallback;
}

type CollisionRow = Collision & {
  vehicle?: {
    companyId?: string;
    status?: VEHICLE_STATUS;
  };
};

function TableCollision() {
  useContext(AuthContext);
  const { notifyError } = useToastContext();
  const navigate = useNavigate();

  const [page, setPage] = useState<number>(1);
  const [data, setData] = useState({ itemsPerPage: 10, totalItems: 0 });
  const [collisions, setCollisions] = useState<Collision[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const [filters, setFilters] = useState<TableFiltersState>({
    search: "",
    status: [],
    filterColumn: "",
    orderBy: "-createdAt",
  });

  const { filterMode, selectedCompanies, selectedRegion, selectedUf } =
    useCompanyStore();
  const filtersItemsPerPage = (filters as Record<string, unknown>).itemsPerPage;


  const apiFilters = useMemo(() => {
    const itemsPerPage = toNumber(
      filtersItemsPerPage,
      data.itemsPerPage || 10
    );

    return {
      search: filters.search || "",
      orderBy: filters.orderBy || "-updatedAt",
      itemsPerPage,
    };
  }, [
    filters.search,
    filters.orderBy,
    filtersItemsPerPage,
    data.itemsPerPage,
  ]);
  // Effect para carregar as colisões sempre que os filtros ou a página mudarem
  useEffect(() => {
    async function loadCollisions() {
      setIsLoading(true);

      try {
        const result = await getCollisions({
          search: apiFilters.search,
          orderBy: apiFilters.orderBy,
          page,
          itemsPerPage: apiFilters.itemsPerPage,
        });

        setData({
          itemsPerPage: apiFilters.itemsPerPage,
          totalItems: result?.totalItems || 0,
        });

        if (!result?.ok) {
          notifyError((result as { message?: string })?.message || "unknown error");
          setCollisions([]);
          return;
        }

        setCollisions((result.data || []) as Collision[]);
      } catch (error) {
        console.error(error);
        notifyError("Erro ao buscar colisões");
        setCollisions([]);
      } finally {
        setIsLoading(false);
      }
    }

    loadCollisions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiFilters, page, filterMode, selectedCompanies, selectedRegion, selectedUf]);

  const handleFilterChange = (newFilters: Partial<TableFiltersState>) => {
    setFilters((prev) => ({
      ...prev,
      ...newFilters,
    }));
    setPage(1);
  };

  return (
    <Suspense fallback={<Loading />}>
      <div className="table-section">
        <TableFilters
          dashboardHeader="Colisões"
          placeholder="Digite a placa ou o parceiro."
          statusOptions={{}}
          hideStatusFilter={true}
          onSearch={handleFilterChange}
          entity="collision"
          tableData={collisions}
        />

        <GenericTable
          headers={headers}
          data={collisions}
          setFilters={({ orderBy }) => {
            setFilters((prev) => ({
              ...prev,
              orderBy,
            }));
          }}
          renders={
            {
              city: (_value: unknown, rowData: unknown) => {
                const row = rowData as CollisionRow;
                return row.city || getCompanySigla(row.vehicle?.companyId || "");
              },

              //
              licensePlate: (_value: unknown, rowData: unknown) => {
                const row = rowData as CollisionRow;
                const plate = row.licensePlate;
                if (!plate) return "-";

                return (
                  <button
                    type="button"
                    onClick={() => navigate(`/veiculos/${plate}`)}
                    className="text-gray-900 font-medium hover:text-black transition-colors"
                    title="Abrir veículo"
                  >
                    {plate}
                  </button>
                );
              },

              partnerName: (partnerName: unknown) => String(partnerName || "-"),
              status: (_value: unknown, rowData: unknown) => {
                const row = rowData as CollisionRow;
                const status = row.vehicle?.status;
                return status ? getStatusLabelsVehicle(status) || "-" : "-";
              },

              value: (value: unknown) =>
                `R$ ${Number(value || 0).toLocaleString("pt-BR", {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}`,

              pieces: (pieces: unknown) => (Array.isArray(pieces) ? pieces.length : 0),
              createdAt: formatDateAndTime,

              // Ação para abrir detalhes da colisão
              actions: (_value: unknown, rowData: unknown) => {
                const row = rowData as CollisionRow;
                return (
                  <ContextMenu
                    data={row}
                    actions={[
                      {
                        label: "Ver detalhes",
                        action: (data) =>
                          navigate(
                            `/dashboard/collision/${(data as CollisionRow)._id || (data as CollisionRow).vehicleId}`
                          ),
                      },
                    ]}
                  />
                );
              },
            }
          }
          entity="collision"
        />

        {isLoading && <Loading />}

        <TableFooter
          page={page || 1}
          totalItems={data.totalItems || 0}
          itemsPerPage={data.itemsPerPage || 10}
          goToPage={setPage}
        />
      </div>
    </Suspense>
  );
}

export default TableCollision;
